import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Button from '../button'
import Icon from '../icon'
import {
  createEmptyServiceHourAllocationRow,
  getServiceHourCategoryLabel,
} from '../../lib/serviceHourCategories'

export { createEmptyServiceHourAllocationRow }

export default function EventServiceHourAllocationsEditor({
  open,
  categories,
  language,
  value,
  onChange,
}) {
  const { t } = useTranslation()

  if (!open || !value?.length) {
    return null
  }

  const rows = value
  const visibleRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row._destroy)

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row,
    )
    onChange(next)
  }

  const addRow = () => {
    onChange([...rows, createEmptyServiceHourAllocationRow()])
  }

  const removeRow = index => {
    const targetRow = rows[index]
    if (!targetRow) return

    const next = targetRow.id
      ? rows.map((row, i) => (i === index ? { ...row, _destroy: true } : row))
      : rows.filter((_, i) => i !== index)

    const hasVisibleRows = next.some(row => !row._destroy)
    onChange(
      hasVisibleRows ? next : [...next, createEmptyServiceHourAllocationRow()],
    )
  }

  return (
    <div className="form-group serviceHourAllocationsEditor ms-4 ps-2 mb-6">
      <div className="title mb-2">
        {t('event.service_hours_allocations.section_title')}
      </div>
      {visibleRows.map(({ row, index }) => (
        <div
          className="row align-items-end mb-2 serviceHourAllocationsEditor__row"
          key={row.clientId ?? row.id ?? index}
        >
          <div className="col-sm-8">
            <label
              className="form-label small text-muted mb-1"
              htmlFor={`service-hour-category-${
                row.clientId ?? row.id ?? index
              }`}
            >
              {t('event.service_hours_allocations.category')}
            </label>
            <select
              className="form-control"
              id={`service-hour-category-${row.clientId ?? row.id ?? index}`}
              value={row.service_hour_category_id ?? ''}
              onChange={e => {
                const v = e.target.value
                updateRow(index, {
                  service_hour_category_id: v === '' ? null : Number(v),
                })
              }}
            >
              <option value="">
                {t('event.service_hours_allocations.category_placeholder')}
              </option>
              {categories
                .filter(cat => {
                  if (row.service_hour_category_id === cat.id) return true
                  return !visibleRows.some(
                    ({ row: _row }) =>
                      _row.service_hour_category_id === cat.id && _row !== row,
                  )
                })
                .map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {getServiceHourCategoryLabel(cat, language)}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-sm-2">
            <label
              className="form-label small text-muted mb-1"
              htmlFor={`service-hour-hours-${row.clientId ?? row.id ?? index}`}
            >
              {t('event.service_hours_allocations.hours')}
            </label>
            <input
              className="form-control"
              id={`service-hour-hours-${row.clientId ?? row.id ?? index}`}
              inputMode="numeric"
              min={1}
              style={{ maxWidth: '5rem' }}
              type="number"
              step={1}
              value={
                row.hours === '' ||
                row.hours == null ||
                row.hours === 0 ||
                row.hours === '0'
                  ? ''
                  : row.hours
              }
              onChange={e => {
                const raw = e.target.value
                if (raw === '') {
                  updateRow(index, { hours: '' })
                  return
                }
                const n = Number.parseInt(raw, 10)
                updateRow(index, {
                  hours: Number.isNaN(n) ? '' : Math.max(1, n),
                })
              }}
            />
          </div>
          <div className="col-sm-2 pb-1">
            {visibleRows.length > 0 && (
              <Button
                type="button"
                variant="outline-secondary"
                className="w-100"
                onClick={() => removeRow(index)}
                aria-label={t('event.service_hours_allocations.remove_row')}
              >
                <Icon name="times" />
              </Button>
            )}
          </div>
        </div>
      ))}
      <div className="mt-2">
        <Button type="button" variant="checkbox" onClick={addRow}>
          <Icon name="plus" />{' '}
          {t('event.service_hours_allocations.add_category')}
        </Button>
      </div>
    </div>
  )
}

EventServiceHourAllocationsEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      category_name_en: PropTypes.string.isRequired,
      category_name_es: PropTypes.string.isRequired,
    }),
  ),
  language: PropTypes.oneOf(['en', 'es']),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.string,
      id: PropTypes.number,
      service_hour_category_id: PropTypes.number,
      hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      _destroy: PropTypes.bool,
    }),
  ),
  onChange: PropTypes.func.isRequired,
}

EventServiceHourAllocationsEditor.defaultProps = {
  categories: [],
  language: 'en',
  value: [],
}
